import * as React from 'react';
import { I18n } from 'react-redux-i18n';

import { FieldSelectorOnChangeMultiple } from '@common/components/FieldSelector/field-selector.types';
import { SkipLogicCondition, SkipLogicConditionOperator } from '@common/components/skip-logic/skip-logic.types';
import { QuestionnaireSkipLogicConditionCreateDto } from '@common/dto/questionnaire/skip-logic/condition-create-dto';
import { generateId } from '@common/utils/common';
import { getUniqueFieldCombination } from '@common/utils/fields';
import { QuestionnaireSkipLogicConditionListDto } from '@common/dto/questionnaire/skip-logic/condition-list-dto';
import { FieldType } from '@common/dto/field/type';
import { QuestionnaireFieldAddDto } from '@common/dto/questionnaire/field/add-dto';
import { QuestionnaireFieldUpdateDto } from '@common/dto/questionnaire/field/update-dto';
import { QuestionnaireFieldListDto } from '@common/dto/questionnaire/field/list-dto';
import { useTextBlock } from '../../../questionnaire-designer/components/text-block/text-block.hook';
import { QuestionnaireConsentTabCreateDto, QuestionnaireConsentTabGetDto, QuestionnaireConsentTabUpdateDto } from '@common/dto/questionnaire/consent-tab';
import { QuestionnaireConsentPageProps } from './page.types';
import { useFieldsWithTextBlock } from '../../../fields-with-text-blocks/fields-with-text-blocks.hooks';
import { UpdatedLocalTexts, UpdatedLocalFields, FieldInList } from '../../../fields-with-text-blocks/fields-with-text-blocks.types';
import { QuestionnaireTextBlockLocalDto } from '@modules/fhq-design-tool/dto/text-block-create-dto';
import { getFhq4QuestionnaireFieldData } from '@modules/fhq-design-tool/services/questionnaires';

export const usePageController = (props: QuestionnaireConsentPageProps) => {
  const { cachedData, onChange, showErrorMessage, getFhq4QuestionnaireField, questionnaireId, consentPageNumber } = props;
  const [fieldChooserOpened, setFieldChooserOpened] = React.useState(false);
  const [editingFieldUniqueCombination, setEditingFieldUniqueCombination] = React.useState<string | undefined>();
  const [familyPagesSkipLogicConditions, setFamilyPagesSkipLogicConditions] = React.useState<SkipLogicCondition[]>([]);

  const initializeData = () => {
    let skipLogicConditions: QuestionnaireSkipLogicConditionListDto[] = [];

    if (cachedData) {
      skipLogicConditions = cachedData.page?.skipLogic || [];
    }
    
    setFamilyPagesSkipLogicConditions(skipLogicConditions.map((x) => ({ ...x, id: generateId() } as SkipLogicCondition)) || []);
  };

  React.useEffect(() => {
    initializeData();
  }, [cachedData?.page?.skipLogic]);

  const fields = cachedData?.page?.fields || [];
  const [editingField, setEditingField] = React.useState<QuestionnaireFieldUpdateDto | QuestionnaireFieldAddDto | undefined>();
  
  const navigateOnFieldEdit = React.useCallback(async (field: FieldInList) => {
    const fieldId = field.original.id;
    try {
      if (questionnaireId && fieldId && cachedData) {
        const fullField = await getFhq4QuestionnaireFieldData(
          questionnaireId,
          consentPageNumber,
          fieldId
        );
        setEditingFieldUniqueCombination(
          getUniqueFieldCombination({
            id: fullField.progenyId,
            context: fullField.context,
            type: fullField.progenyFieldType,
          })
        );

        setEditingField(fullField);
      }
    } catch (err) {
      console.error(err);
      showErrorMessage("Failed to load field data");
    }
  }, []);

  const processedFields = React.useMemo(() => {
    return fields
      .map((x): FieldInList => ({
        id: getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }),
        name: x.name,
        original: x,
      }))
      .sort((a, b) => a.original.order - b.original.order);
  }, [fields]);

  const { getCombinedItems } = useFieldsWithTextBlock<FieldInList, QuestionnaireTextBlockLocalDto>({ fields: processedFields, texts: (cachedData?.page?.texts || []) as Array<QuestionnaireTextBlockLocalDto> });
  const combinedItems = React.useMemo(getCombinedItems, [processedFields, cachedData?.page?.texts]);

  const handleDataChange = React.useCallback(
    (dataToUpdate: Partial<QuestionnaireConsentTabUpdateDto> | Partial<QuestionnaireConsentTabCreateDto>) => {
      if (!cachedData) return;
  
      const nextPage = dataToUpdate.page
        ? {
            ...cachedData.page,
            ...dataToUpdate.page,
            modified: true,
          }
        : cachedData.page;
  
      const { enabled: _consentEnabled, ...cachedDataWithoutTransient } =
        (cachedData ?? {}) as QuestionnaireConsentTabGetDto & { enabled?: boolean };
  
      onChange({
        ...cachedDataWithoutTransient,
        ...dataToUpdate,
        page: nextPage,
      });
    },
    [cachedData, onChange]
  );


  const handleUseSkipLogicChange = React.useCallback((val: string) => {
    if (!cachedData?.page) return;

    handleDataChange({
      page: {
        ...cachedData.page,
        skipLogicEnabled: JSON.parse(val),
      },
    });
  }, [cachedData?.page, handleDataChange]);

  const handleSkipLogicConditionAdd = React.useCallback(({ conditions }: { conditions: QuestionnaireSkipLogicConditionCreateDto[] }) => {
    if (!cachedData?.page) return;

    const familyPagesSkipLogic = [
      ...familyPagesSkipLogicConditions,
      ...conditions.map((condition) => ({ ...condition, id: generateId(), operator: SkipLogicConditionOperator.equals } as SkipLogicCondition)),
    ];

    handleDataChange({
      page: {
        ...cachedData.page,
        skipLogic: familyPagesSkipLogic.map(({ id, ...condition }) => condition as QuestionnaireSkipLogicConditionListDto),
      },
    });
    setFamilyPagesSkipLogicConditions(familyPagesSkipLogic);
  }, [cachedData?.page, handleDataChange, familyPagesSkipLogicConditions]);

  const handleSkipLogicChange = React.useCallback((familyPagesSkipLogic: SkipLogicCondition[]) => {
    if (!cachedData?.page) return;

    handleDataChange({
      page: {
        ...cachedData.page,
        skipLogic: familyPagesSkipLogic.map(({ id, ...condition }) => condition as QuestionnaireSkipLogicConditionCreateDto),
      },
    });
    setFamilyPagesSkipLogicConditions(familyPagesSkipLogic);
  }, [cachedData?.page, handleDataChange]);

  const openFieldChooser = React.useCallback(() => {
    setFieldChooserOpened(true);
  }, []);

  const closeFieldChooser = React.useCallback(() => {
    setFieldChooserOpened(false);
  }, []);

  const handleFieldAdd = React.useCallback(
    async ({ fields }: FieldSelectorOnChangeMultiple) => {
      const newFields: Array<QuestionnaireFieldAddDto | QuestionnaireFieldListDto> = [];
      
      await Promise.all(
        fields.map(async (field, fieldIndex) => {
          const hasSameField = cachedData?.page?.fields?.some(
            (x) =>
              getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) ===
              getUniqueFieldCombination({ id: field.fieldId, context: field.fieldContext, type: field.fieldTypeId })
          );

          if (hasSameField || !cachedData) {
            return;
          }

          try {
            const fullField = await getFhq4QuestionnaireField({
              id: field.fieldId,
              context: field.fieldContext,
              system: field.systemField,
            });
            
            const newField: QuestionnaireFieldAddDto | QuestionnaireFieldListDto = {
              progenyId: field.fieldId as number,
              name: field.fieldName,
              path: fullField.path || fullField.name,
              order: combinedItems.length + fieldIndex + 1,
              systemField: field.systemField,
              context: field.fieldContext,
              type: fullField.type,
              progenyFieldType: field.fieldTypeId as FieldType,
              description: fullField.description,
              required: fullField.required,
              readonly: fullField.readonly,
              defaultValue: fullField.defaultValue,
              allowDuplications: fullField.allowDuplications,
              skipLogic: fullField.skipLogic,
              options: fullField.options,
              subfields: fullField.subfields,
              skipLogicEnabled: fullField.skipLogicEnabled,
              displayInFHQ: fullField.displayInFHQ,
              possibleDropdownOptions: fullField.possibleDropdownOptions,
              modified: true
            };

            newFields.push(newField);
          } catch (err) {
            console.error(err);
            showErrorMessage(`${I18n.t("Failed to get field with fieldName", { fieldName: field.fieldName })}`);
          }
        })
      );

      if (!newFields.length) {
        return;
      }

      if (!cachedData?.page) return;

      handleDataChange({
        page: {
          ...cachedData.page,
          fields: [...(cachedData.page.fields ?? []), ...newFields],
        },
      });
    },
    [cachedData, handleDataChange, showErrorMessage, getFhq4QuestionnaireField, combinedItems]
  );

  const handleFieldsOrderChange = React.useCallback((newFieldsOrder: Array<FieldInList>) => {
    if (!cachedData?.page) return;

    handleDataChange({
      page: {
        ...cachedData.page,
        fields: newFieldsOrder.map((x, i) => ({
          ...x.original,
          order: i + 1,
        })),
      },
    });
  }, [cachedData?.page, handleDataChange]);

  const handleFieldDelete = React.useCallback((field: FieldInList) => {
    if (!cachedData?.page) return;

    const nextFields = cachedData.page.fields
      ?.filter(
        (x) =>
          getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) !==
          getUniqueFieldCombination({ id: field.original.progenyId, context: field.original.context, type: field.original.progenyFieldType })
      )
      .sort((a, b) => a.order - b.order)
      .map((f, i) => ({
        ...f,
        order: i + 1,
      }));

    handleDataChange({
      page: {
        ...cachedData.page,
        fields: nextFields,
      },
    });
  }, [cachedData?.page, handleDataChange]);

  const handleFieldChange = React.useCallback((field: QuestionnaireFieldUpdateDto | QuestionnaireFieldAddDto) => {
    if (!cachedData?.page) return;

    setEditingField(field);

    handleDataChange({
      page: {
        ...cachedData.page,
        fields: cachedData.page.fields?.map((x) =>
          getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) ===
          getUniqueFieldCombination({ id: field.progenyId, context: field.context, type: field.progenyFieldType })
            ? field
            : x
        ),
      },
    });
  }, [cachedData?.page, handleDataChange]);

  const goBackFromFieldEdit = React.useCallback(() => {
    setEditingFieldUniqueCombination(undefined);
    setEditingField(undefined);
  }, []);

  const canShowHideField = React.useCallback(() => {
    return false;
  }, []);

  const pageForTextBlocks = React.useMemo(() => {
    if (!cachedData?.page) return undefined;

    return {
      ...cachedData.page,
      texts: (cachedData.page.texts ?? []) as QuestionnaireTextBlockLocalDto[],
    };
  }, [cachedData?.page]);

  const {
    editingTextBlock,
    addTextBlock,
    openTextBlockEditing,
    closeTextBlockEditing,
    handleTextBlockChange,
    handleTextBlockDelete,
    handleOrderChange,
  } = useTextBlock({
    data: pageForTextBlocks,
    onChange: (pageWithTexts) => {
      if (!cachedData?.page) return;

      handleDataChange({
        page: {
          ...cachedData.page,
          ...pageWithTexts,
        },
      });
    },
  });

  const handleAddTextBlock = React.useCallback(() => {
    addTextBlock({ startOrder: combinedItems.length });
  }, [addTextBlock, combinedItems]);

  const handleFieldsOrTextBlocksChange = React.useCallback((params: {
    texts: UpdatedLocalTexts<QuestionnaireTextBlockLocalDto>;
    fields: UpdatedLocalFields<FieldInList>;
  }) => {
    if (!cachedData?.page) return;

    handleDataChange({
      page: {
        ...cachedData.page,
        texts: params.texts,
        fields: params.fields.map(({ order, ...field }) => ({
          ...field.original,
          order,
          modified: field.original.modified || field.original.order !== order,
        })),
      },
    });
  }, [cachedData?.page, handleDataChange]);

  const [buttonsSkipLogicConditions, setButtonsSkipLogicConditions] = React.useState<SkipLogicCondition[]>([]);

  React.useEffect(() => {
    const buttonConditions = cachedData?.consentButtonsSkipLogic ?? [];
    setButtonsSkipLogicConditions(
      buttonConditions.map((x) => ({ ...x, id: generateId() } as SkipLogicCondition))
    );
  }, [cachedData?.consentButtonsSkipLogic]);

  const handleUseConsentButtonsSkipLogicChange = React.useCallback((val: string) => {
    const shouldUseSkipLogic = val === "true";

    handleDataChange({
      useConsentButtonsSkipLogic: shouldUseSkipLogic,
      ...(shouldUseSkipLogic ? {} : { consentButtonsSkipLogic: [] }),
    });

    if (!shouldUseSkipLogic) {
      setButtonsSkipLogicConditions([]);
    }
  }, [handleDataChange]);

  const handleConsentButtonsSkipLogicConditionAdd = React.useCallback(({ conditions }: { conditions: QuestionnaireSkipLogicConditionCreateDto[] }) => {
    const nextConditions = [
      ...buttonsSkipLogicConditions,
      ...conditions.map((condition) => ({ ...condition, id: generateId(), operator: SkipLogicConditionOperator.equals } as SkipLogicCondition)),
    ];

    handleDataChange({
      consentButtonsSkipLogic: nextConditions.map(({ id, ...condition }) => condition as QuestionnaireSkipLogicConditionListDto),
    });
    setButtonsSkipLogicConditions(nextConditions);
  }, [buttonsSkipLogicConditions, handleDataChange]);

  const handleConsentButtonsSkipLogicChange = React.useCallback((nextConditions: SkipLogicCondition[]) => {
    handleDataChange({
      consentButtonsSkipLogic: nextConditions.map(({ id, ...condition }) => condition as QuestionnaireSkipLogicConditionCreateDto),
    });
    setButtonsSkipLogicConditions(nextConditions);
  }, [handleDataChange]);

  return {
    processedFields,
    familyPagesSkipLogicConditions,
    handleSkipLogicChange,
    handleSkipLogicConditionAdd,
    handleUseSkipLogicChange,
    buttonsSkipLogicConditions,
    handleConsentButtonsSkipLogicChange,
    handleConsentButtonsSkipLogicConditionAdd,
    handleUseConsentButtonsSkipLogicChange,
    handleFieldChange,
    goBackFromFieldEdit,
    editingField,
    handleFieldAdd,
    openFieldChooser,
    closeFieldChooser,
    fieldChooserOpened,
    handleFieldsOrderChange,
    handleFieldDelete,
    navigateOnFieldEdit,
    canShowHideField,
    editingTextBlock,
    addTextBlock,
    handleOrderChange,
    handleTextBlockDelete,
    openTextBlockEditing,
    closeTextBlockEditing,
    handleTextBlockChange,
    handleFieldsOrTextBlocksChange,
    handleAddTextBlock,
    combinedItems
  };
};